import * as Sentry from '@sentry/react';
import { call, put, select } from 'redux-saga/effects';
import { HttpError } from 'api/apiClient';
import {
  acquireLease,
  archive,
  create,
  getAccess,
  getOne,
  releaseLease,
  update,
} from 'api/cloudsave/cloudsaveApi';
import { getCurrentUserId } from 'auth/userIdentity';
import {
  fileSavedMessage,
  makeACopyMessage,
} from 'containers/Snackbar/snackbarMessages';
import type { RootState } from 'store';
import { getIsOnline } from 'store/connection/selectors';
import { setFileHasBeenEdited, setFileStatus } from 'store/fileState/reducer';
import { FileStatus } from 'store/fileState/types';
import { withRetry } from 'store/sagaUtils';
import { addMessage, removeMessagesByType } from 'store/snackbar/reducer';
import { MessageType } from 'store/snackbar/types';
import { storageUnitCache } from 'util/cache/storageUnitCacheIndexedDB';
import {
  archiveStorageUnitSuccess,
  createStorageUnitSuccess,
  setCurrentFile,
  updateStorageUnitSuccess,
} from './reducer';
import type {
  AccessRead,
  CurrentFile,
  StorageUnit,
  StorageUnitCreate,
  StorageUnitUpdate,
} from './types';
import { LeaseType, StorageUnitType } from './types';
import {
  mergeServerStorageUnitWithSubmittedContent,
  mergeSyncedStorageUnit,
  normalizeLeases,
  toIsoString,
} from './utils';
import { clearCachedStorageUnitTree } from './storageUnitStateUtils';

type PendingStorageUnitAction = NonNullable<StorageUnit['pendingAction']>;

let syncingPending = false;

function* syncPendingStorageUnits() {
  if (syncingPending) {
    return;
  }
  syncingPending = true;
  try {
    const pending: {
      entry: {
        id: string;
        pendingAction: PendingStorageUnitAction;
        syncedAt?: string | null;
        localUpdatedAt: string;
      };
      storageUnit: StorageUnit;
    }[] = yield call([storageUnitCache, storageUnitCache.getPendingSync]);

    if (!pending.length) {
      return;
    }

    const pendingCreateIds = new Set(
      pending
        .filter(({ entry }) => entry.pendingAction === 'create')
        .map(({ storageUnit }) => storageUnit.id),
    );
    const getCreateDepth = (
      storageUnit: StorageUnit,
      visited = new Set<string>(),
    ): number => {
      if (
        !storageUnit.parentId ||
        !pendingCreateIds.has(storageUnit.parentId) ||
        visited.has(storageUnit.parentId)
      ) {
        return 0;
      }

      const parent = pending.find(
        (item) => item.storageUnit.id === storageUnit.parentId,
      );
      if (!parent) {
        return 0;
      }

      return (
        1 +
        getCreateDepth(
          parent.storageUnit,
          new Set([...visited, storageUnit.parentId]),
        )
      );
    };

    const sortedPending = [...pending].sort((left, right) => {
      const priority = (action: PendingStorageUnitAction) => {
        if (action === 'create') {
          return 0;
        }
        if (action === 'update') {
          return 1;
        }
        return 2;
      };
      const priorityDiff =
        priority(left.entry.pendingAction) -
        priority(right.entry.pendingAction);
      if (priorityDiff !== 0) {
        return priorityDiff;
      }

      if (
        left.entry.pendingAction === 'create' &&
        right.entry.pendingAction === 'create'
      ) {
        return (
          getCreateDepth(left.storageUnit) - getCreateDepth(right.storageUnit)
        );
      }

      return 0;
    });

    const currentFile: CurrentFile = yield select(
      (state: RootState) => state.storageUnitsState.currentFile,
    );
    const syncedTempIds = new Map<string, string>();

    for (const pendingItem of sortedPending) {
      const { entry, storageUnit } = pendingItem;
      if (entry.pendingAction === 'create') {
        if (!storageUnit.id.startsWith('tempId-')) {
          continue;
        }
        const parentId = storageUnit.parentId
          ? (syncedTempIds.get(storageUnit.parentId) ?? storageUnit.parentId)
          : undefined;
        if (parentId?.startsWith('tempId-')) {
          continue;
        }
        try {
          const body: StorageUnitCreate = {
            parentId,
            name: storageUnit.name,
            type: storageUnit.type,
            fileData: storageUnit.fileData,
            thumbnail: storageUnit.thumbnail,
            status: storageUnit.status,
            projectMetadata: storageUnit.project,
            flowsheetMetadata: storageUnit.flowsheetMetadata,
          };

          const { parsedBody } = yield create(body);
          let merged: StorageUnit = parsedBody;
          if (
            merged &&
            merged.fileData === undefined &&
            storageUnit.fileData !== undefined
          ) {
            merged = {
              ...merged,
              fileData: storageUnit.fileData,
            };
          }
          if (
            merged &&
            merged.thumbnail === undefined &&
            storageUnit.thumbnail !== undefined
          ) {
            merged = {
              ...merged,
              thumbnail: storageUnit.thumbnail,
            };
          }
          if (
            merged &&
            merged.project === undefined &&
            storageUnit.project !== undefined
          ) {
            merged = {
              ...merged,
              project: storageUnit.project,
            };
          }
          if (merged && merged.parentId === undefined && parentId) {
            merged = {
              ...merged,
              parentId,
            };
          }
          if (merged && storageUnit.flowsheetMetadata) {
            const {
              parsedBody: updatedStorageUnit,
            }: { parsedBody: StorageUnit } = yield update({
              id: merged.id,
              type: StorageUnitType.File,
              flowsheetMetadata: storageUnit.flowsheetMetadata,
            });
            merged = mergeServerStorageUnitWithSubmittedContent(
              updatedStorageUnit,
              storageUnit,
            );
          }
          try {
            const { parsedBody: access }: { parsedBody: AccessRead } =
              yield getAccess(parsedBody.id);
            merged = {
              ...merged,
              access: {
                indirect: access.indirect.map((u) => u.id),
                direct: access.direct.map((u) => u.id),
              },
            };
          } catch (error) {
            Sentry.captureException(error, {
              tags: { saga: 'syncPendingStorageUnits', action: 'create' },
              extra: { id: parsedBody.id },
              level: 'warning',
            });
          }

          const normalized: StorageUnit | null =
            yield storageUnitCache.replaceTempId(storageUnit.id, merged);

          if (!normalized) {
            continue;
          }

          syncedTempIds.set(storageUnit.id, normalized.id);
          for (const childPendingItem of sortedPending) {
            if (childPendingItem.storageUnit.parentId !== storageUnit.id) {
              continue;
            }

            const updatedChild = {
              ...childPendingItem.storageUnit,
              parentId: normalized.id,
            };
            childPendingItem.storageUnit = updatedChild;
            yield storageUnitCache.setOne(updatedChild, {
              markSynced: false,
              pendingAction: childPendingItem.entry.pendingAction,
              syncedAt: childPendingItem.entry.syncedAt ?? null,
              localUpdatedAt: childPendingItem.entry.localUpdatedAt,
            });
          }

          yield storageUnitCache.clearList();
          yield put(
            createStorageUnitSuccess({
              tempId: storageUnit.id,
              storageUnit: normalized,
            }),
          );

          if (currentFile.id === storageUnit.id) {
            const normalizedCurrent = normalizeLeases(normalized);
            yield put(
              setCurrentFile({
                id: normalizedCurrent.id,
                name: normalizedCurrent.name,
                status: normalizedCurrent.status,
                updatedAt: normalizedCurrent.updatedAt,
                isSynced: normalizedCurrent.isSynced,
                syncedAt: normalizedCurrent.syncedAt,
                exclusiveLease: normalizedCurrent.exclusiveLease,
                softLeases: normalizedCurrent.softLeases ?? [],
                access: normalizedCurrent.access,
              }),
            );
            yield put(removeMessagesByType(MessageType.FILE_SAVED));
            yield put(addMessage(fileSavedMessage()));
          }
        } catch (error) {
          Sentry.captureException(error, {
            tags: { saga: 'syncPendingStorageUnits', action: 'create' },
            extra: { id: storageUnit.id },
          });
        }
        continue;
      }

      if (entry.pendingAction === 'update') {
        if (storageUnit.id.startsWith('tempId-')) {
          continue;
        }
        let acquiredLeaseId: string | undefined;
        try {
          const { parsedBody: remote }: { parsedBody: StorageUnit } =
            yield getOne(storageUnit.id);
          const remoteUpdatedAt = toIsoString(
            remote.updatedAt ?? remote.createdAt,
          );
          const localSyncedAt =
            entry.syncedAt ?? toIsoString(storageUnit.syncedAt);

          if (
            remoteUpdatedAt &&
            localSyncedAt &&
            new Date(remoteUpdatedAt).getTime() >
              new Date(localSyncedAt).getTime()
          ) {
            yield put(removeMessagesByType(MessageType.FILE_SAVED));
            yield put(
              addMessage(
                makeACopyMessage(
                  storageUnit.id,
                  storageUnit.name ?? '',
                  undefined,
                  {
                    useConflictDraft: true,
                  },
                ),
              ),
            );
            if (currentFile.id === storageUnit.id) {
              yield put(setFileStatus(FileStatus.READ_ONLY));
            }
            continue;
          }

          const currentUserId = getCurrentUserId();
          const { exclusiveLease, softLeases } = normalizeLeases(remote);
          const holdsLease =
            exclusiveLease?.userId === currentUserId ||
            softLeases?.some((lease) => lease.userId === currentUserId);

          if (!holdsLease) {
            try {
              yield withRetry(() =>
                acquireLease(storageUnit.id, LeaseType.Soft),
              );
              acquiredLeaseId = storageUnit.id;
            } catch (leaseError) {
              if (
                !(leaseError instanceof HttpError) ||
                leaseError.status !== 403
              ) {
                throw leaseError;
              }
              yield put(removeMessagesByType(MessageType.FILE_SAVED));
              yield put(removeMessagesByType(MessageType.MAKE_A_COPY));
              yield put(
                addMessage(
                  makeACopyMessage(
                    storageUnit.id,
                    storageUnit.name ?? '',
                    exclusiveLease?.userName,
                    {
                      useConflictDraft: true,
                    },
                  ),
                ),
              );
              if (currentFile.id === storageUnit.id) {
                yield put(setFileStatus(FileStatus.READ_ONLY));
              }
              continue;
            }
          }

          const updatePayload: StorageUnitUpdate = {
            id: storageUnit.id,
            parentId: storageUnit.parentId,
            name: storageUnit.name,
            type: storageUnit.type,
            fileData: storageUnit.fileData,
            thumbnail: storageUnit.thumbnail,
            projectMetadata: storageUnit.project,
            flowsheetMetadata: storageUnit.flowsheetMetadata,
          };

          const { parsedBody } = yield withRetry(() => update(updatePayload));

          const mergedSyncedPayload: StorageUnit = mergeSyncedStorageUnit(
            parsedBody,
            storageUnit,
          );

          const synced: StorageUnit = yield storageUnitCache.setOne(
            mergedSyncedPayload,
            {
              syncedAt:
                toIsoString(parsedBody.updatedAt ?? parsedBody.createdAt) ??
                toIsoString(
                  storageUnit.localUpdatedAt ??
                    storageUnit.updatedAt ??
                    storageUnit.createdAt,
                ),
            },
          );
          yield storageUnitCache.clearList();
          yield put(updateStorageUnitSuccess(synced));

          if (currentFile.id === storageUnit.id) {
            yield put(
              setCurrentFile({
                id: synced.id,
                name: synced.name,
                status: synced.status,
                updatedAt: synced.updatedAt,
                isSynced: synced.isSynced,
                syncedAt: synced.syncedAt,
                exclusiveLease: synced.exclusiveLease,
                softLeases: synced.softLeases ?? [],
                access: synced.access,
              }),
            );
            yield put(setFileHasBeenEdited(false));
            yield put(removeMessagesByType(MessageType.FILE_SAVED));
            yield put(addMessage(fileSavedMessage()));
          }
        } catch (error) {
          Sentry.captureException(error, {
            tags: { saga: 'syncPendingStorageUnits', action: 'update' },
            extra: { id: storageUnit.id },
          });
        } finally {
          if (acquiredLeaseId && currentFile.id !== acquiredLeaseId) {
            yield releaseLease(acquiredLeaseId).catch(() => undefined);
          }
        }
        continue;
      }

      if (entry.pendingAction === 'archive') {
        if (storageUnit.id.startsWith('tempId-')) {
          yield* clearCachedStorageUnitTree(storageUnit.id);
          yield storageUnitCache.clearList();
          yield put(archiveStorageUnitSuccess(storageUnit.id));
          continue;
        }

        try {
          const { parsedBody }: { parsedBody: StorageUnit } = yield withRetry(
            () => archive(storageUnit.id),
          );
          const syncedArchivePayload = parsedBody
            ? mergeSyncedStorageUnit(parsedBody, storageUnit)
            : storageUnit;
          const synced: StorageUnit = yield storageUnitCache.setOne({
            ...syncedArchivePayload,
            archived: true,
          });

          yield storageUnitCache.clearList();
          yield put(archiveStorageUnitSuccess(synced.id));
        } catch (error) {
          Sentry.captureException(error, {
            tags: { saga: 'syncPendingStorageUnits', action: 'archive' },
            extra: { id: storageUnit.id },
          });
        }
      }
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: { saga: 'syncPendingStorageUnits' },
    });
  } finally {
    syncingPending = false;
  }
}

export function* handleReconnectSync() {
  const isOnline: boolean = yield select(getIsOnline);
  if (!isOnline) {
    return;
  }
  yield put(removeMessagesByType(MessageType.FILE_SAVED));
  yield call(syncPendingStorageUnits);
}
